import { Box, Typography, Button } from "@mui/material";
import { Lock, Refresh } from "@mui/icons-material";
import { useSnackbar } from "./snackbar";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useState } from "react";

const API_URL = process.env.REACT_APP_API_URL;

interface AccessExpiredProps {
    classroom_id: string;
}

export default function AccessExpired({ classroom_id }: AccessExpiredProps) {
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();

    const [loading, setLoading] = useState(false);

    const [paymentUrl, setPaymentUrl] = useState<string | null>(null);

    const handleRenew = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_URL}/utils/students/payment-url?classroom_id=${classroom_id}`,
                { withCredentials: true }
            );
            setPaymentUrl(response.data);
            window.open(response.data, "_blank");
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleApiError = (error: any) => {
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };


    return (
        <Box
            sx={{
                height: "100vh",
                zIndex: 1000,
                background: "rgba(234, 234, 235, 0.85)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                borderRadius: 3,
                gap: 2,
                px: 3,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        width: 90,
                        height: 90,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(99,179,237,0.35), rgba(167,139,250,0.2))",
                        filter: "blur(18px)",
                    }}
                />
                <Box
                    sx={{
                        width: 68,
                        height: 68,
                        borderRadius: "20px",
                        background: "rgba(255,255,255,0.7)",
                        border: "1px solid rgba(255,255,255,0.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 24px rgba(99,179,237,0.2)",
                        position: "relative",
                    }}
                >
                    <Lock sx={{ fontSize: 30, color: "#5a8dee" }} />
                </Box>
            </Box>

            <Typography
                variant="h6"
                sx={{
                    fontWeight: 700,
                    color: "#2d3748",
                    textAlign: "center",
                    letterSpacing: "-0.3px",
                }}
            >
                Access Expired
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    color: "#718096",
                    textAlign: "center",
                    maxWidth: 280,
                    lineHeight: 1.8,
                }}
            >
                Your access to this classroom has expired. Renew to continue learning.
            </Typography>

            <Button
                variant="contained"
                size="large"
                onClick={handleRenew}
                disabled={loading}
                startIcon={!loading && <Refresh />}
                sx={{
                    mt: 1,
                    px: 5,
                    py: 1.4,
                    borderRadius: "12px",
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                    "&:hover": {
                        backgroundColor: "#1565c0",
                        boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                    },
                    "&:disabled": { opacity: 0.6 },
                }}
            >
                {loading ? "Please wait..." : "Renew Access"}
            </Button>

            {paymentUrl && (
                <Typography
                    variant="caption"
                    sx={{ color: "#a0aec0", textAlign: "center", mt: 0.5 }}
                >
                    Didn't redirect?{" "}
                    <Typography
                        component="a"
                        href={paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ color: "#5a8dee", fontWeight: 600, textDecoration: "underline" }}
                    >
                        Open payment page
                    </Typography>
                </Typography>
            )}
        </Box>
    );
}
